/**
 * adminGaps2Builder — 2ª onda de gaps do admin (fechar TODOS os endpoints restantes).
 *
 * Agrega slices de contextos variados (DI é detalhe de infra, não fronteira de domínio):
 * Oferta+Diário (enrollment), e nas próximas ondas Protocolo workflow / AssinaturaContrato
 * (acadadmin), NFS-e / Cobrança PSP / Bolsa (commerce). Um spread único no container.
 *
 * @since G.5.19
 */
import type { $Fetch } from 'ofetch';

import { HttpOfertaGateway, HttpDiarioAulaGateway } from '@/infrastructure/http/HttpOfertaGateways';
import { HttpProtocoloWorkflowGateway } from '@/infrastructure/http/HttpProtocoloWorkflowGateway';
import {
  ListOfertas, CreateOferta, UpdateOferta, MudarStatusOferta,
  ListAulas, RegistrarAula, AtualizarAula, ExcluirAula,
} from '@/application/usecase/OfertaUseCaseImpl';
import {
  GetProtocolo, ListProtocoloThread, ListProtocoloTransferencias, AbrirProtocolo,
  ResponderProtocolo, EncerrarProtocolo, ReabrirProtocolo, TransferirProtocolo,
  ListProtocoloAnexos, UploadProtocoloAnexo,
} from '@/application/usecase/ProtocoloWorkflowUseCaseImpl';
import { HttpBolsaGateway } from '@/infrastructure/http/HttpBolsaGateway';
import { HttpNfseGateway, HttpCobrancaGatewayGateway } from '@/infrastructure/http/HttpFaturamentoGateways';
import { HttpParcelaArtifactGateway } from '@/infrastructure/http/HttpParcelaArtifactGateway';
import { HttpContratoAssinaturaGateway } from '@/infrastructure/http/HttpContratoAssinaturaGateway';
import { SolicitarBolsa, AprovarBolsa, RevogarBolsa } from '@/application/usecase/BolsaUseCaseImpl';
import { EmitirNfse, ConsultarNfse, EmitirCobrancaGateway } from '@/application/usecase/FaturamentoUseCaseImpl';
import { IssueParcelaPix, IssueParcelaBoleto } from '@/application/usecase/ParcelaArtifactUseCaseImpl';
import {
  GetContratoAssinatura, GerarContratoAssinatura, RecusarContrato, RevogarContrato,
} from '@/application/usecase/ContratoAssinaturaUseCaseImpl';

export function buildAdminGaps2Slice(httpClient: $Fetch) {
  const ofertaGateway = new HttpOfertaGateway(httpClient);
  const diarioGateway = new HttpDiarioAulaGateway(httpClient);
  const protocoloWfGateway = new HttpProtocoloWorkflowGateway(httpClient);
  const bolsaGateway = new HttpBolsaGateway(httpClient);
  const nfseGateway = new HttpNfseGateway(httpClient);
  const cobrancaGateway = new HttpCobrancaGatewayGateway(httpClient);
  const parcelaArtifactGateway = new HttpParcelaArtifactGateway(httpClient);
  const contratoAssinaturaGateway = new HttpContratoAssinaturaGateway(httpClient);

  const useCases = {
    listOfertas: new ListOfertas(ofertaGateway),
    createOferta: new CreateOferta(ofertaGateway),
    updateOferta: new UpdateOferta(ofertaGateway),
    mudarStatusOferta: new MudarStatusOferta(ofertaGateway),
    listAulas: new ListAulas(diarioGateway),
    registrarAula: new RegistrarAula(diarioGateway),
    atualizarAula: new AtualizarAula(diarioGateway),
    excluirAula: new ExcluirAula(diarioGateway),
    getProtocolo: new GetProtocolo(protocoloWfGateway),
    listProtocoloThread: new ListProtocoloThread(protocoloWfGateway),
    listProtocoloTransferencias: new ListProtocoloTransferencias(protocoloWfGateway),
    abrirProtocolo: new AbrirProtocolo(protocoloWfGateway),
    responderProtocolo: new ResponderProtocolo(protocoloWfGateway),
    encerrarProtocolo: new EncerrarProtocolo(protocoloWfGateway),
    reabrirProtocolo: new ReabrirProtocolo(protocoloWfGateway),
    transferirProtocolo: new TransferirProtocolo(protocoloWfGateway),
    listProtocoloAnexos: new ListProtocoloAnexos(protocoloWfGateway),
    uploadProtocoloAnexo: new UploadProtocoloAnexo(protocoloWfGateway),
    solicitarBolsa: new SolicitarBolsa(bolsaGateway),
    aprovarBolsa: new AprovarBolsa(bolsaGateway),
    revogarBolsa: new RevogarBolsa(bolsaGateway),
    emitirNfse: new EmitirNfse(nfseGateway),
    consultarNfse: new ConsultarNfse(nfseGateway),
    emitirCobrancaGateway: new EmitirCobrancaGateway(cobrancaGateway),
    issueParcelaPix: new IssueParcelaPix(parcelaArtifactGateway),
    issueParcelaBoleto: new IssueParcelaBoleto(parcelaArtifactGateway),
    getContratoAssinatura: new GetContratoAssinatura(contratoAssinaturaGateway),
    gerarContratoAssinatura: new GerarContratoAssinatura(contratoAssinaturaGateway),
    recusarContrato: new RecusarContrato(contratoAssinaturaGateway),
    revogarContrato: new RevogarContrato(contratoAssinaturaGateway),
  };

  return { useCases };
}
